import type { ReactNode } from "react";
import { Redirect, useLocation } from "wouter";
import { erpMenuForPath, hasOnlyMenuGrantAccess } from "@eurostorm/shared";
import { useAuth } from "../hooks/useAuth.js";
import { IdleTimeout } from "./IdleTimeout.js";

interface ProtectedRouteProps {
  children: ReactNode;
  requireSuperAdmin?: boolean;
  /** Exige admin em PELO MENOS UMA empresa (super admin também passa). */
  requireAdmin?: boolean;
  /**
   * F-finance-viewer-access: módulo financeiro (Tesouraria) — acesso a super
   * admin OU papel `viewer` (sócio) em ≥1 empresa. Admin/user comuns não entram.
   */
  requireFinance?: boolean;
  /**
   * F-tech-portal — rota do portal do técnico: papel `technician` em ≥1
   * empresa (super admin também passa, para suporte).
   */
  requireTechnician?: boolean;
  /** F-chantier-manager — rota do portal do chantier: papel `manager`. */
  requireManager?: boolean;
  /** F-chantier-agenda — rota do portal do cliente: papel `client`. */
  requireClient?: boolean;
}

/** Usuário cujas ÚNICAS memberships ativas são de técnico → só o portal. */
function isTechnicianOnly(user: {
  isSuperAdmin: boolean;
  memberships: Array<{ active: boolean; role: string }>;
}): boolean {
  if (user.isSuperAdmin) return false;
  const active = user.memberships.filter((m) => m.active);
  return active.length > 0 && active.every((m) => m.role === "technician");
}

/** F-chantier-agenda — só cliente → só o portal /cliente. */
function isClientOnly(user: {
  isSuperAdmin: boolean;
  memberships: Array<{ active: boolean; role: string }>;
}): boolean {
  if (user.isSuperAdmin) return false;
  const active = user.memberships.filter((m) => m.active);
  return active.length > 0 && active.every((m) => m.role === "client");
}

/** F-chantier-manager — só gerente de projeto → só o portal /chantier. */
function isManagerOnly(user: {
  isSuperAdmin: boolean;
  memberships: Array<{ active: boolean; role: string }>;
}): boolean {
  if (user.isSuperAdmin) return false;
  const active = user.memberships.filter((m) => m.active);
  return active.length > 0 && active.every((m) => m.role === "manager");
}

export function ProtectedRoute({
  children,
  requireSuperAdmin,
  requireAdmin,
  requireFinance,
  requireTechnician,
  requireManager,
  requireClient,
}: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const [location] = useLocation();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-400">
        Carregando…
      </div>
    );
  }
  if (!user) {
    return <Redirect to="/login" />;
  }
  // F-acesso-por-menu (CEO 2026-10-12) — usuário de portal com menus do
  // Gestion liberados: entra SÓ nas rotas dos menus liberados (e em /conta);
  // qualquer outra rota do ERP volta para o portal dele.
  const soPorMenu = hasOnlyMenuGrantAccess(user);
  const rotaDePortal = requireTechnician || requireManager || requireClient;
  if (soPorMenu && !rotaDePortal && location !== "/conta") {
    const menu = erpMenuForPath(location);
    const liberado = menu != null && user.menuGrants.some((g) => g.menu === menu);
    if (!liberado) {
      return (
        <Redirect
          to={isManagerOnly(user) ? "/chantier" : isTechnicianOnly(user) ? "/portal" : "/cliente"}
        />
      );
    }
    return (
      <>
        <IdleTimeout />
        {children}
      </>
    );
  }
  // F-tech-portal — conta só-técnico não entra no ERP: qualquer rota que não
  // seja o portal redireciona para /portal (inclui o pós-login /dashboard).
  if (!requireTechnician && isTechnicianOnly(user)) {
    return <Redirect to="/portal" />;
  }
  if (!requireManager && isManagerOnly(user)) {
    return <Redirect to="/chantier" />;
  }
  if (!requireClient && isClientOnly(user)) {
    return <Redirect to="/cliente" />;
  }
  if (requireClient && !user.isSuperAdmin) {
    const isCli = user.memberships.some((m) => m.active && m.role === "client");
    if (!isCli) return <Redirect to="/dashboard" />;
  }
  if (requireManager && !user.isSuperAdmin) {
    const isMgr = user.memberships.some((m) => m.active && m.role === "manager");
    if (!isMgr) return <Redirect to="/dashboard" />;
  }
  if (requireTechnician && !user.isSuperAdmin) {
    const isTech = user.memberships.some((m) => m.active && m.role === "technician");
    if (!isTech) return <Redirect to="/dashboard" />;
  }
  if (requireSuperAdmin && !user.isSuperAdmin) {
    return <Redirect to="/dashboard" />;
  }
  if (requireAdmin && !user.isSuperAdmin) {
    const hasAdmin = user.memberships.some((m) => m.active && m.role === "admin");
    if (!hasAdmin) return <Redirect to="/dashboard" />;
  }
  if (requireFinance && !user.isSuperAdmin) {
    const isViewer = user.memberships.some((m) => m.active && m.role === "viewer");
    if (!isViewer) return <Redirect to="/dashboard" />;
  }
  return (
    <>
      <IdleTimeout />
      {children}
    </>
  );
}
